import {Player} from "@lottiefiles/react-lottie-player";
import prepareTheBunniesEscape from "../../../assets/json/3.1-prepare-the-bunnies-escape.json";
import {getRandomColor} from "../../../utils/GetRandomColor";
import IconText from "../../IconText";
import {faPersonRunning} from "@fortawesome/free-solid-svg-icons";

export default function PrepareTheBunniesEscape() {

    const lottieStyle = {
        border: `5px solid ${getRandomColor()}`,
    };

    return (
        <div className={"blog-body"}>
            <h1><IconText text={"3.1. Prepare the Bunnies' Escape"} icon={faPersonRunning}/></h1>

            <h2>Question</h2>
            <div className={"blog-quote"}>
                <p>
                    You're awfully close to destroying the LAMBCHOP doomsday device and freeing Commander Lambda's
                    bunny
                    workers, but once they're free of the work duties the bunnies are going to need to escape
                    Lambda's space
                    station via the escape pods as quickly as possible. Unfortunately, the halls of the space
                    station are a
                    maze of corridors and dead ends that will be a deathtrap for the escaping bunnies. Fortunately,
                    Commander Lambda has put you in charge of a remodeling project that will give you the
                    opportunity to
                    make things a little easier for the bunnies. Unfortunately (again), you can't just remove all
                    obstacles
                    between the bunnies and the escape pods - at most you can remove one wall per escape pod path,
                    both to
                    maintain structural integrity of the station and to avoid arousing Commander Lambda's
                    suspicions.
                </p>
                <p>
                    You have maps of parts of the space station, each starting at a work area exit and ending at the
                    door to
                    an escape pod. The map is represented as a matrix of 0s and 1s, where 0s are passable space and
                    1s are
                    impassable walls. The door out of the station is at the top left (0,0) and the door into an
                    escape pod
                    is at the bottom right (w-1,h-1).
                </p>
                <p>
                    Write a function solution(map) that generates the length of the shortest path from the station
                    door to
                    the escape pod, where you are allowed to remove one wall as part of your remodeling plans. The
                    path
                    length is the total number of nodes you pass through, counting both the entrance and exit nodes.
                    The
                    starting and ending positions are always passable (0). The map will always be solvable, though
                    you may
                    or may not need to remove a wall. The height and width of the map can be from 2 to 20. Moves can
                    only be
                    made in cardinal directions; no diagonal moves are allowed.
                </p>
                <p>
                    <b>Test cases</b>
                </p>
                <p>
                    Input: <br/>
                    solution.solution([[0, 1, 1, 0], [0, 0, 0, 1], [1, 1, 0, 0], [1, 1, 1, 0]]) <br/>
                    Output: <br/>
                    7 <br/><br/>

                    Input: <br/>
                    solution.solution([[0, 0, 0, 0, 0, 0], [1, 1, 1, 1, 1, 0], [0, 0, 0, 0, 0, 0], [0, 1, 1, 1, 1,
                    1], [0,
                    1, 1, 1, 1, 1], [0, 0, 0, 0, 0, 0]]) <br/>
                    Output: <br/>
                    11 <br/>
                </p>
            </div>

            <h2>Solution</h2>
            <p>
                Here is where things get interesting. After this question, the difficulty of the questions increases
                rapidly, which makes them more fun to solve.
            </p>

            <p>
                My approach to this question is to create every single possible maze and use brute force. I defined
                a
                function called <code>wall_breaker</code>, which creates mazes by replacing a different 1 with 0
                every step.
                Then we can
                feed the <code>astar</code> function, which solves the mazes by using the astar algorithm. After
                that we can
                return the
                shortest one.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={prepareTheBunniesEscape} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>

            <p>
                At first, I tried solving the mazes using the BFS algorithm, but it failed some of the test cases.
                Later, I
                noticed that BFS fails to solve the mazes unless the path is some sort of tunnel.
            </p>

            <h2>Code</h2>
            <pre><code className="language-python">{`def astar(map):
    """
    A* algorithm to find the shortest path in a binary maze,
    starting from the top left and ending at the bottom right.
    """
    start = (0, 0)
    goal = (len(map) - 1, len(map[0]) - 1)

    # Defining movement directions: up, down, left, right
    directions = [(-1, 0), (1, 0), (0, -1), (0, 1)]

    # Initializing open and closed lists
    open_list = []
    closed_set = set()

    # Adding start node to open list
    open_list.append((0, start, 0))  # (total_cost, node, g_cost)

    while open_list:
        open_list.sort(key=lambda x: x[0])  # Sort by total_cost
        total_cost, current_node, g_cost = open_list.pop(0)
        closed_set.add(current_node)

        # Checking if current node is goal node
        if current_node == goal:
            return g_cost + 1

        # Generating successors (neighbors) of current node
        for dr, dc in directions:
            neighbor_row, neighbor_col = current_node[0] + dr, current_node[1] + dc

            # Checking if neighbor is within bounds and not a wall
            if 0 <= neighbor_row < len(map) and 0 <= neighbor_col < len(map[0]) and map[neighbor_row][neighbor_col] == 0:
                neighbor_node = (neighbor_row, neighbor_col)
                # Calculating heuristic (h value)
                heuristic = abs(neighbor_row - goal[0]) + abs(neighbor_col - goal[1])
                # Calculating total cost (f value)
                total_cost = g_cost + 1 + heuristic  # Cost to move to neighbor is 1
                # Checking if neighbor is already in closed set
                if neighbor_node in closed_set:
                    continue
                # Checking if neighbor is already in open list and has a lower total cost
                for i, (tc, n, _) in enumerate(open_list):
                    if n == neighbor_node and total_cost >= tc:
                        break
                else:
                    open_list.append((total_cost, neighbor_node, g_cost + 1))

    # If no path found
    return len(map) * len(map[0])

def wall_breaker(map):
    """
    For a given map, generates all possible maps with
    breaking one wall at a time (Converting 1 to 0) and returns the dictionary
    of the newly generated maps.
    """
    new_maps = {}
                    # Before creating new maps, we append the original map to the dictionary
    new_maps["original_map"] = map

    for i in range(len(map)):
        for j in range(len(map[0])):
            if map[i][j] == 1:
                new_map = [row[:] for row in map]
                new_map[i][j] = 0
                new_maps["map_{0}_{1}".format(i, j)] = new_map

    return new_maps

def solution(map):
    # Get all possible maps by breaking one wall at a time
    new_maps = wall_breaker(map)

    # Finding the shortest path for each map
    shortest_paths = {}

                    for key, value in new_maps.items():
        shortest_paths[key] = astar(value)

    # Returning the minimum path length
    return min(shortest_paths.values())`}</code></pre>
        </div>
    );
}